// Países LATAM + USA con sus provincias/estados/departamentos y principales
// ciudades. Usado por el formulario de crear/editar búsquedas para que el
// recruiter elija país → provincia → ciudad de forma consistente entre tenants.
//
// Argentina mantiene la lista exhaustiva existente en `locations-ar.ts` (la
// re-exportamos para no duplicar). Otros países: top 3-8 ciudades por
// provincia/estado (capitales + ciudades más pobladas + relevantes). El
// typeahead acepta texto libre cuando la ciudad no está en la lista, así no
// bloqueamos al recruiter.

import { AR_PROVINCES, AR_CITIES_BY_PROVINCE } from "./locations-ar";

export type CountryCode =
  | "AR" | "BO" | "BR" | "CL" | "CO" | "CR" | "CU" | "DO" | "EC" | "SV"
  | "GT" | "HN" | "MX" | "NI" | "PA" | "PY" | "PE" | "PR" | "UY" | "VE"
  | "US";

export type Country = { code: CountryCode; name: string };

// Orden: Argentina primero (default), luego LATAM alfabético, USA al final.
export const COUNTRIES: readonly Country[] = [
  { code: "AR", name: "Argentina" },
  { code: "BO", name: "Bolivia" },
  { code: "BR", name: "Brasil" },
  { code: "CL", name: "Chile" },
  { code: "CO", name: "Colombia" },
  { code: "CR", name: "Costa Rica" },
  { code: "CU", name: "Cuba" },
  { code: "DO", name: "República Dominicana" },
  { code: "EC", name: "Ecuador" },
  { code: "SV", name: "El Salvador" },
  { code: "GT", name: "Guatemala" },
  { code: "HN", name: "Honduras" },
  { code: "MX", name: "México" },
  { code: "NI", name: "Nicaragua" },
  { code: "PA", name: "Panamá" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Perú" },
  { code: "PR", name: "Puerto Rico" },
  { code: "UY", name: "Uruguay" },
  { code: "VE", name: "Venezuela" },
  { code: "US", name: "Estados Unidos" },
] as const;

/**
 * Provincias/estados/departamentos por país, con sus principales ciudades.
 * - Argentina reusa `AR_CITIES_BY_PROVINCE` (lista exhaustiva existente).
 * - Otros países: top 3-8 ciudades por provincia. El typeahead acepta texto
 *   libre cuando la ciudad no está en la lista.
 */
export const PROVINCES_BY_COUNTRY: Record<CountryCode, Record<string, readonly string[]>> = {
  AR: AR_CITIES_BY_PROVINCE,

  BO: {
    "La Paz":     ["La Paz", "El Alto", "Viacha", "Achocalla"],
    "Santa Cruz": ["Santa Cruz de la Sierra", "Montero", "Warnes", "La Guardia", "Camiri"],
    "Cochabamba": ["Cochabamba", "Quillacollo", "Sacaba", "Tiquipaya"],
    "Oruro":      ["Oruro", "Huanuni", "Challapata"],
    "Potosí":     ["Potosí", "Llallagua", "Uyuni", "Tupiza"],
    "Tarija":     ["Tarija", "Yacuiba", "Villamontes", "Bermejo"],
    "Chuquisaca": ["Sucre", "Monteagudo", "Camargo"],
    "Beni":       ["Trinidad", "Riberalta", "Guayaramerín"],
    "Pando":      ["Cobija", "Porvenir"],
  },

  BR: {
    "Acre":              ["Rio Branco", "Cruzeiro do Sul"],
    "Alagoas":           ["Maceió", "Arapiraca", "Palmeira dos Índios"],
    "Amapá":             ["Macapá", "Santana"],
    "Amazonas":          ["Manaus", "Parintins", "Itacoatiara"],
    "Bahia":             ["Salvador", "Feira de Santana", "Vitória da Conquista", "Camaçari", "Ilhéus"],
    "Ceará":             ["Fortaleza", "Caucaia", "Juazeiro do Norte", "Maracanaú"],
    "Distrito Federal":  ["Brasília", "Taguatinga", "Ceilândia"],
    "Espírito Santo":    ["Vitória", "Vila Velha", "Serra", "Cariacica"],
    "Goiás":             ["Goiânia", "Aparecida de Goiânia", "Anápolis", "Rio Verde"],
    "Maranhão":          ["São Luís", "Imperatriz", "Caxias", "Timon"],
    "Mato Grosso":       ["Cuiabá", "Várzea Grande", "Rondonópolis", "Sinop"],
    "Mato Grosso do Sul":["Campo Grande", "Dourados", "Três Lagoas", "Corumbá"],
    "Minas Gerais":      ["Belo Horizonte", "Uberlândia", "Contagem", "Juiz de Fora", "Betim", "Montes Claros"],
    "Pará":              ["Belém", "Ananindeua", "Santarém", "Marabá"],
    "Paraíba":           ["João Pessoa", "Campina Grande", "Santa Rita"],
    "Paraná":            ["Curitiba", "Londrina", "Maringá", "Ponta Grossa", "Cascavel", "Foz do Iguaçu"],
    "Pernambuco":        ["Recife", "Jaboatão dos Guararapes", "Olinda", "Caruaru", "Petrolina"],
    "Piauí":             ["Teresina", "Parnaíba", "Picos"],
    "Rio de Janeiro":    ["Rio de Janeiro", "São Gonçalo", "Duque de Caxias", "Nova Iguaçu", "Niterói", "Campos dos Goytacazes", "Petrópolis"],
    "Rio Grande do Norte":["Natal", "Mossoró", "Parnamirim"],
    "Rio Grande do Sul": ["Porto Alegre", "Caxias do Sul", "Pelotas", "Canoas", "Santa Maria"],
    "Rondônia":          ["Porto Velho", "Ji-Paraná", "Ariquemes"],
    "Roraima":           ["Boa Vista"],
    "Santa Catarina":    ["Florianópolis", "Joinville", "Blumenau", "Chapecó", "Itajaí"],
    "São Paulo":         ["São Paulo", "Guarulhos", "Campinas", "São Bernardo do Campo", "Santo André", "Osasco", "Ribeirão Preto", "Sorocaba", "Santos", "Bauru"],
    "Sergipe":           ["Aracaju", "Nossa Senhora do Socorro"],
    "Tocantins":         ["Palmas", "Araguaína", "Gurupi"],
  },

  CL: {
    "Arica y Parinacota":          ["Arica"],
    "Tarapacá":                    ["Iquique", "Alto Hospicio"],
    "Antofagasta":                 ["Antofagasta", "Calama", "Tocopilla"],
    "Atacama":                     ["Copiapó", "Vallenar", "Chañaral"],
    "Coquimbo":                    ["La Serena", "Coquimbo", "Ovalle"],
    "Valparaíso":                  ["Valparaíso", "Viña del Mar", "Quilpué", "Villa Alemana", "San Antonio"],
    "Metropolitana de Santiago":   ["Santiago", "Puente Alto", "Maipú", "La Florida", "Las Condes", "Providencia", "Ñuñoa", "Lo Barnechea"],
    "Libertador General Bernardo O'Higgins": ["Rancagua", "San Fernando", "Machalí"],
    "Maule":                       ["Talca", "Curicó", "Linares"],
    "Ñuble":                       ["Chillán"],
    "Biobío":                      ["Concepción", "Talcahuano", "Los Ángeles", "Chiguayante"],
    "La Araucanía":                ["Temuco", "Padre Las Casas", "Villarrica"],
    "Los Ríos":                    ["Valdivia", "La Unión"],
    "Los Lagos":                   ["Puerto Montt", "Osorno", "Castro", "Ancud"],
    "Aysén":                       ["Coyhaique", "Puerto Aysén"],
    "Magallanes":                  ["Punta Arenas", "Puerto Natales"],
  },

  CO: {
    "Amazonas":          ["Leticia"],
    "Antioquia":         ["Medellín", "Bello", "Itagüí", "Envigado", "Sabaneta", "Rionegro"],
    "Arauca":            ["Arauca", "Tame"],
    "Atlántico":         ["Barranquilla", "Soledad", "Malambo", "Sabanagrande"],
    "Bogotá D.C.":       ["Bogotá"],
    "Bolívar":           ["Cartagena", "Magangué", "Turbaco"],
    "Boyacá":            ["Tunja", "Duitama", "Sogamoso", "Chiquinquirá"],
    "Caldas":            ["Manizales", "La Dorada", "Chinchiná"],
    "Caquetá":           ["Florencia", "San Vicente del Caguán"],
    "Casanare":          ["Yopal", "Aguazul"],
    "Cauca":             ["Popayán", "Santander de Quilichao"],
    "Cesar":             ["Valledupar", "Aguachica", "Codazzi"],
    "Chocó":             ["Quibdó", "Istmina"],
    "Córdoba":           ["Montería", "Lorica", "Sahagún"],
    "Cundinamarca":      ["Soacha", "Facatativá", "Zipaquirá", "Chía", "Fusagasugá"],
    "Guainía":           ["Inírida"],
    "Guaviare":          ["San José del Guaviare"],
    "Huila":             ["Neiva", "Pitalito", "Garzón"],
    "La Guajira":        ["Riohacha", "Maicao", "Uribia"],
    "Magdalena":         ["Santa Marta", "Ciénaga", "Fundación"],
    "Meta":              ["Villavicencio", "Acacías", "Granada"],
    "Nariño":            ["Pasto", "Tumaco", "Ipiales"],
    "Norte de Santander":["Cúcuta", "Ocaña", "Pamplona"],
    "Putumayo":          ["Mocoa", "Puerto Asís"],
    "Quindío":           ["Armenia", "Calarcá", "Montenegro"],
    "Risaralda":         ["Pereira", "Dosquebradas", "Santa Rosa de Cabal"],
    "San Andrés y Providencia":["San Andrés"],
    "Santander":         ["Bucaramanga", "Floridablanca", "Girón", "Barrancabermeja", "Piedecuesta"],
    "Sucre":             ["Sincelejo", "Corozal", "Tolú"],
    "Tolima":            ["Ibagué", "Espinal", "Honda"],
    "Valle del Cauca":   ["Cali", "Palmira", "Buenaventura", "Tuluá", "Cartago", "Yumbo"],
    "Vaupés":            ["Mitú"],
    "Vichada":           ["Puerto Carreño"],
  },

  CR: {
    "San José":   ["San José", "Alajuelita", "Desamparados", "Escazú", "Curridabat"],
    "Alajuela":   ["Alajuela", "San Carlos", "San Ramón", "Grecia"],
    "Cartago":    ["Cartago", "Turrialba", "Paraíso"],
    "Heredia":    ["Heredia", "San Pablo", "Belén"],
    "Guanacaste": ["Liberia", "Nicoya", "Santa Cruz"],
    "Puntarenas": ["Puntarenas", "Quepos", "Golfito"],
    "Limón":      ["Limón", "Siquirres", "Guápiles"],
  },

  CU: {
    "La Habana":          ["La Habana"],
    "Artemisa":           ["Artemisa", "Bauta", "San Antonio de los Baños"],
    "Mayabeque":          ["San José de las Lajas", "Güines"],
    "Matanzas":           ["Matanzas", "Cárdenas", "Varadero"],
    "Villa Clara":        ["Santa Clara", "Sagua la Grande", "Caibarién"],
    "Cienfuegos":         ["Cienfuegos", "Cumanayagua"],
    "Sancti Spíritus":    ["Sancti Spíritus", "Trinidad"],
    "Ciego de Ávila":     ["Ciego de Ávila", "Morón"],
    "Camagüey":           ["Camagüey", "Florida", "Nuevitas"],
    "Las Tunas":          ["Las Tunas", "Puerto Padre"],
    "Granma":             ["Bayamo", "Manzanillo"],
    "Holguín":            ["Holguín", "Banes", "Moa"],
    "Santiago de Cuba":   ["Santiago de Cuba", "Palma Soriano"],
    "Guantánamo":         ["Guantánamo", "Baracoa"],
    "Pinar del Río":      ["Pinar del Río", "Consolación del Sur"],
    "Isla de la Juventud":["Nueva Gerona"],
  },

  DO: {
    "Distrito Nacional":     ["Santo Domingo"],
    "Santo Domingo":         ["Santo Domingo Este", "Santo Domingo Norte", "Santo Domingo Oeste", "Boca Chica"],
    "Santiago":              ["Santiago de los Caballeros", "Tamboril", "Licey al Medio"],
    "La Vega":               ["Concepción de La Vega", "Constanza", "Jarabacoa"],
    "San Cristóbal":         ["San Cristóbal", "Bajos de Haina"],
    "Puerto Plata":          ["Puerto Plata", "Sosúa", "Imbert"],
    "San Pedro de Macorís":  ["San Pedro de Macorís"],
    "La Romana":             ["La Romana"],
    "Duarte":                ["San Francisco de Macorís"],
    "Espaillat":             ["Moca"],
    "María Trinidad Sánchez":["Nagua"],
    "Hermanas Mirabal":      ["Salcedo"],
    "Samaná":                ["Samaná", "Las Terrenas"],
    "Monte Cristi":          ["Monte Cristi"],
    "Valverde":              ["Mao"],
    "Santiago Rodríguez":    ["Sabaneta"],
    "Dajabón":               ["Dajabón"],
    "Monseñor Nouel":        ["Bonao"],
    "Sánchez Ramírez":       ["Cotuí"],
    "Azua":                  ["Azua de Compostela"],
    "Peravia":               ["Baní"],
    "San José de Ocoa":      ["San José de Ocoa"],
    "Barahona":              ["Barahona"],
    "Bahoruco":              ["Neiba"],
    "Independencia":         ["Jimaní"],
    "Pedernales":            ["Pedernales"],
    "El Seibo":              ["Santa Cruz de El Seibo"],
    "Hato Mayor":            ["Hato Mayor del Rey"],
    "La Altagracia":         ["Higüey", "Punta Cana", "Bávaro"],
    "Monte Plata":           ["Monte Plata"],
    "San Juan":              ["San Juan de la Maguana"],
    "Elías Piña":            ["Comendador"],
  },

  EC: {
    "Azuay":         ["Cuenca", "Gualaceo", "Paute"],
    "Bolívar":       ["Guaranda", "San Miguel"],
    "Cañar":         ["Azogues", "La Troncal", "Cañar"],
    "Carchi":        ["Tulcán", "San Gabriel"],
    "Chimborazo":    ["Riobamba", "Guano", "Alausí"],
    "Cotopaxi":      ["Latacunga", "La Maná", "Salcedo"],
    "El Oro":        ["Machala", "Pasaje", "Santa Rosa", "Huaquillas"],
    "Esmeraldas":    ["Esmeraldas", "Quinindé", "Atacames"],
    "Galápagos":     ["Puerto Ayora", "Puerto Baquerizo Moreno"],
    "Guayas":        ["Guayaquil", "Durán", "Daule", "Milagro", "Samborondón", "Playas"],
    "Imbabura":      ["Ibarra", "Otavalo", "Cotacachi", "Atuntaqui"],
    "Loja":          ["Loja", "Catamayo", "Cariamanga"],
    "Los Ríos":      ["Babahoyo", "Quevedo", "Ventanas"],
    "Manabí":        ["Portoviejo", "Manta", "Chone", "Jipijapa", "Bahía de Caráquez"],
    "Morona Santiago":["Macas", "Sucúa"],
    "Napo":          ["Tena", "Archidona"],
    "Orellana":      ["Puerto Francisco de Orellana", "La Joya de los Sachas"],
    "Pastaza":       ["Puyo", "Mera"],
    "Pichincha":     ["Quito", "Sangolquí", "Cayambe", "Machachi", "Tabacundo"],
    "Santa Elena":   ["Santa Elena", "La Libertad", "Salinas"],
    "Santo Domingo de los Tsáchilas":["Santo Domingo"],
    "Sucumbíos":     ["Nueva Loja"],
    "Tungurahua":    ["Ambato", "Baños", "Pelileo"],
    "Zamora Chinchipe":["Zamora", "Yantzaza"],
  },

  SV: {
    "San Salvador":  ["San Salvador", "Soyapango", "Mejicanos", "Apopa", "Ciudad Delgado"],
    "La Libertad":   ["Santa Tecla", "Nuevo Cuscatlán", "Antiguo Cuscatlán", "Quezaltepeque"],
    "Santa Ana":     ["Santa Ana", "Chalchuapa", "Metapán"],
    "Sonsonate":     ["Sonsonate", "Acajutla", "Izalco"],
    "Ahuachapán":    ["Ahuachapán", "Atiquizaya"],
    "La Paz":        ["Zacatecoluca", "San Luis Talpa"],
    "Cuscatlán":     ["Cojutepeque", "Suchitoto"],
    "Cabañas":       ["Sensuntepeque", "Ilobasco"],
    "San Vicente":   ["San Vicente", "Apastepeque"],
    "Usulután":      ["Usulután", "Jiquilisco"],
    "San Miguel":    ["San Miguel", "Chinameca"],
    "Morazán":       ["San Francisco Gotera", "Jocoro"],
    "La Unión":      ["La Unión", "Santa Rosa de Lima"],
    "Chalatenango":  ["Chalatenango", "Nueva Concepción"],
  },

  GT: {
    "Guatemala":        ["Ciudad de Guatemala", "Mixco", "Villa Nueva", "San Miguel Petapa", "Chinautla"],
    "Sacatepéquez":     ["Antigua Guatemala", "Jocotenango", "Ciudad Vieja"],
    "Chimaltenango":    ["Chimaltenango", "Tecpán Guatemala"],
    "Escuintla":        ["Escuintla", "Puerto San José", "Santa Lucía Cotzumalguapa"],
    "Santa Rosa":       ["Cuilapa", "Barberena"],
    "Sololá":           ["Sololá", "Panajachel", "Santiago Atitlán"],
    "Totonicapán":      ["Totonicapán"],
    "Quetzaltenango":   ["Quetzaltenango", "Coatepeque"],
    "Suchitepéquez":    ["Mazatenango"],
    "Retalhuleu":       ["Retalhuleu"],
    "San Marcos":       ["San Marcos", "Malacatán"],
    "Huehuetenango":    ["Huehuetenango", "Chiantla"],
    "Quiché":           ["Santa Cruz del Quiché", "Chichicastenango"],
    "Baja Verapaz":     ["Salamá"],
    "Alta Verapaz":     ["Cobán", "Chisec"],
    "Petén":            ["Flores", "San Benito", "Poptún"],
    "Izabal":           ["Puerto Barrios", "Morales", "Livingston"],
    "Zacapa":           ["Zacapa", "Río Hondo"],
    "Chiquimula":       ["Chiquimula", "Esquipulas"],
    "Jalapa":           ["Jalapa"],
    "Jutiapa":          ["Jutiapa"],
    "El Progreso":      ["Guastatoya"],
  },

  HN: {
    "Francisco Morazán": ["Tegucigalpa", "Comayagüela", "Talanga"],
    "Cortés":            ["San Pedro Sula", "Choloma", "Puerto Cortés", "Villanueva", "La Lima"],
    "Atlántida":         ["La Ceiba", "Tela", "El Progreso"],
    "Yoro":              ["El Progreso", "Yoro", "Olanchito"],
    "Comayagua":         ["Comayagua", "Siguatepeque"],
    "Choluteca":         ["Choluteca", "El Triunfo"],
    "Olancho":           ["Juticalpa", "Catacamas"],
    "El Paraíso":        ["Yuscarán", "Danlí"],
    "Copán":             ["Santa Rosa de Copán", "Copán Ruinas"],
    "Lempira":           ["Gracias"],
    "Intibucá":          ["La Esperanza"],
    "Santa Bárbara":     ["Santa Bárbara"],
    "Ocotepeque":        ["Ocotepeque", "Nueva Ocotepeque"],
    "La Paz":            ["La Paz"],
    "Valle":             ["Nacaome", "San Lorenzo"],
    "Colón":             ["Trujillo", "Tocoa"],
    "Gracias a Dios":    ["Puerto Lempira"],
    "Islas de la Bahía": ["Roatán", "Coxen Hole"],
  },

  MX: {
    "Aguascalientes":   ["Aguascalientes", "Jesús María", "Calvillo"],
    "Baja California":  ["Tijuana", "Mexicali", "Ensenada", "Tecate", "Rosarito"],
    "Baja California Sur":["La Paz", "Los Cabos", "San José del Cabo", "Loreto"],
    "Campeche":         ["San Francisco de Campeche", "Ciudad del Carmen"],
    "Chiapas":          ["Tuxtla Gutiérrez", "Tapachula", "San Cristóbal de Las Casas", "Comitán"],
    "Chihuahua":        ["Ciudad Juárez", "Chihuahua", "Cuauhtémoc", "Delicias"],
    "Ciudad de México": ["Ciudad de México", "Coyoacán", "Iztapalapa", "Tlalpan", "Benito Juárez", "Cuauhtémoc", "Miguel Hidalgo"],
    "Coahuila":         ["Saltillo", "Torreón", "Monclova", "Piedras Negras"],
    "Colima":           ["Colima", "Manzanillo", "Tecomán"],
    "Durango":          ["Durango", "Gómez Palacio", "Lerdo"],
    "Estado de México": ["Toluca", "Ecatepec", "Naucalpan", "Tlalnepantla", "Chimalhuacán", "Nezahualcóyotl"],
    "Guanajuato":       ["León", "Irapuato", "Celaya", "Salamanca", "San Miguel de Allende", "Guanajuato"],
    "Guerrero":         ["Chilpancingo", "Acapulco", "Iguala", "Zihuatanejo"],
    "Hidalgo":          ["Pachuca", "Tulancingo", "Tula de Allende"],
    "Jalisco":          ["Guadalajara", "Zapopan", "Tlaquepaque", "Tonalá", "Puerto Vallarta"],
    "Michoacán":        ["Morelia", "Uruapan", "Zamora", "Lázaro Cárdenas"],
    "Morelos":          ["Cuernavaca", "Cuautla", "Jiutepec"],
    "Nayarit":          ["Tepic", "Bahía de Banderas", "Xalisco"],
    "Nuevo León":       ["Monterrey", "Guadalupe", "San Nicolás de los Garza", "Apodaca", "General Escobedo", "San Pedro Garza García"],
    "Oaxaca":           ["Oaxaca de Juárez", "Salina Cruz", "Tuxtepec"],
    "Puebla":           ["Puebla", "Tehuacán", "San Andrés Cholula", "Atlixco"],
    "Querétaro":        ["Santiago de Querétaro", "San Juan del Río", "Corregidora"],
    "Quintana Roo":     ["Cancún", "Chetumal", "Playa del Carmen", "Cozumel", "Tulum"],
    "San Luis Potosí":  ["San Luis Potosí", "Soledad de Graciano Sánchez", "Ciudad Valles"],
    "Sinaloa":          ["Culiacán", "Mazatlán", "Los Mochis", "Guasave"],
    "Sonora":           ["Hermosillo", "Ciudad Obregón", "Nogales", "San Luis Río Colorado"],
    "Tabasco":          ["Villahermosa", "Cárdenas", "Comalcalco"],
    "Tamaulipas":       ["Reynosa", "Matamoros", "Nuevo Laredo", "Tampico", "Ciudad Victoria"],
    "Tlaxcala":         ["Tlaxcala", "Apizaco", "Huamantla"],
    "Veracruz":         ["Veracruz", "Xalapa", "Coatzacoalcos", "Córdoba", "Orizaba", "Boca del Río"],
    "Yucatán":          ["Mérida", "Valladolid", "Progreso"],
    "Zacatecas":        ["Zacatecas", "Guadalupe", "Fresnillo"],
  },

  NI: {
    "Managua":      ["Managua", "Ciudad Sandino", "Tipitapa"],
    "León":         ["León", "Nagarote"],
    "Chinandega":   ["Chinandega", "Corinto", "El Viejo"],
    "Masaya":       ["Masaya", "Nindirí"],
    "Granada":      ["Granada", "Diriá"],
    "Carazo":       ["Jinotepe", "Diriamba"],
    "Rivas":        ["Rivas", "San Juan del Sur"],
    "Boaco":        ["Boaco", "Camoapa"],
    "Chontales":    ["Juigalpa", "Santo Tomás"],
    "Estelí":       ["Estelí", "Condega"],
    "Madriz":       ["Somoto"],
    "Nueva Segovia":["Ocotal", "Jalapa"],
    "Jinotega":     ["Jinotega", "San Rafael del Norte"],
    "Matagalpa":    ["Matagalpa", "Sébaco"],
    "Río San Juan": ["San Carlos"],
    "RACCN":        ["Bilwi (Puerto Cabezas)", "Waspán"],
    "RACCS":        ["Bluefields", "El Bluff"],
  },

  PA: {
    "Bocas del Toro": ["Bocas del Toro", "Changuinola"],
    "Chiriquí":       ["David", "Boquete", "Volcán"],
    "Coclé":          ["Penonomé", "Aguadulce", "Antón"],
    "Colón":          ["Colón", "Sabanitas"],
    "Darién":         ["La Palma", "Yaviza"],
    "Herrera":        ["Chitré"],
    "Los Santos":     ["Las Tablas", "Guararé"],
    "Panamá":         ["Panamá", "San Miguelito", "Tocumen", "Pedregal"],
    "Panamá Oeste":   ["La Chorrera", "Arraiján", "Capira"],
    "Veraguas":       ["Santiago de Veraguas", "Atalaya"],
    "Comarca Emberá-Wounaan":["Cirilo Guainora"],
    "Comarca Guna Yala":      ["El Porvenir"],
    "Comarca Ngäbe-Buglé":    ["Llano Tugrí"],
  },

  PY: {
    "Asunción":         ["Asunción"],
    "Central":          ["San Lorenzo", "Luque", "Capiatá", "Lambaré", "Fernando de la Mora", "Limpio", "Ñemby"],
    "Alto Paraná":      ["Ciudad del Este", "Presidente Franco", "Hernandarias", "Minga Guazú"],
    "Itapúa":           ["Encarnación", "Hohenau", "Coronel Bogado"],
    "Cordillera":       ["Caacupé", "Eusebio Ayala"],
    "Caaguazú":         ["Coronel Oviedo", "Caaguazú"],
    "Paraguarí":        ["Paraguarí", "Yaguarón"],
    "San Pedro":        ["San Pedro de Ycuamandiyú", "Santa Rosa del Aguaray"],
    "Misiones":         ["San Juan Bautista", "Santa Rosa Misiones"],
    "Ñeembucú":         ["Pilar"],
    "Amambay":          ["Pedro Juan Caballero"],
    "Canindeyú":        ["Salto del Guairá"],
    "Concepción":       ["Concepción", "Horqueta"],
    "Guairá":           ["Villarrica", "Coronel Martínez"],
    "Caazapá":          ["Caazapá"],
    "Alto Paraguay":    ["Fuerte Olimpo"],
    "Boquerón":         ["Filadelfia", "Loma Plata"],
    "Presidente Hayes": ["Villa Hayes", "Benjamín Aceval"],
  },

  PE: {
    "Lima":          ["Lima", "Callao", "San Juan de Lurigancho", "Ate", "Comas", "Villa El Salvador", "San Martín de Porres", "Miraflores", "San Isidro", "Barranco"],
    "Arequipa":      ["Arequipa", "Cerro Colorado", "Yanahuara"],
    "La Libertad":   ["Trujillo", "Chepén", "Otuzco"],
    "Piura":         ["Piura", "Sullana", "Talara", "Paita"],
    "Lambayeque":    ["Chiclayo", "Lambayeque", "Ferreñafe"],
    "Cusco":         ["Cusco", "Sicuani", "Urubamba"],
    "Junín":         ["Huancayo", "Tarma", "Jauja"],
    "Áncash":        ["Huaraz", "Chimbote", "Casma"],
    "Cajamarca":     ["Cajamarca", "Jaén", "Chota"],
    "Loreto":        ["Iquitos", "Yurimaguas", "Nauta"],
    "Ica":           ["Ica", "Chincha Alta", "Pisco", "Nazca"],
    "San Martín":    ["Tarapoto", "Moyobamba", "Rioja"],
    "Puno":          ["Puno", "Juliaca", "Ilave"],
    "Ucayali":       ["Pucallpa", "Aguaytía"],
    "Tacna":         ["Tacna"],
    "Apurímac":      ["Abancay", "Andahuaylas"],
    "Ayacucho":      ["Ayacucho", "Huanta"],
    "Huancavelica":  ["Huancavelica"],
    "Huánuco":       ["Huánuco", "Tingo María"],
    "Madre de Dios": ["Puerto Maldonado"],
    "Moquegua":      ["Moquegua", "Ilo"],
    "Pasco":         ["Cerro de Pasco"],
    "Tumbes":        ["Tumbes", "Zarumilla"],
    "Amazonas":      ["Chachapoyas", "Bagua"],
    "Callao":        ["Callao", "Bellavista", "La Perla"],
  },

  PR: {
    "Puerto Rico": ["San Juan", "Bayamón", "Carolina", "Ponce", "Caguas", "Guaynabo", "Arecibo", "Toa Baja", "Mayagüez", "Trujillo Alto", "Aguadilla", "Fajardo", "Humacao", "Vega Baja", "Manatí"],
  },

  UY: {
    "Montevideo":     ["Montevideo"],
    "Canelones":      ["Canelones", "Las Piedras", "Pando", "Ciudad de la Costa", "La Paz"],
    "Maldonado":      ["Maldonado", "Punta del Este", "San Carlos", "Piriápolis"],
    "Rocha":          ["Rocha", "La Paloma", "Chuy"],
    "Treinta y Tres": ["Treinta y Tres"],
    "Cerro Largo":    ["Melo", "Río Branco"],
    "Rivera":         ["Rivera", "Tranqueras"],
    "Artigas":        ["Artigas", "Bella Unión"],
    "Salto":          ["Salto"],
    "Paysandú":       ["Paysandú"],
    "Río Negro":      ["Fray Bentos", "Young"],
    "Soriano":        ["Mercedes", "Dolores"],
    "Colonia":        ["Colonia del Sacramento", "Carmelo", "Nueva Helvecia"],
    "San José":       ["San José de Mayo", "Ciudad del Plata"],
    "Flores":         ["Trinidad"],
    "Florida":        ["Florida", "Sarandí Grande"],
    "Durazno":        ["Durazno", "Sarandí del Yi"],
    "Lavalleja":      ["Minas"],
    "Tacuarembó":     ["Tacuarembó", "Paso de los Toros"],
  },

  VE: {
    "Distrito Capital":["Caracas"],
    "Amazonas":        ["Puerto Ayacucho"],
    "Anzoátegui":      ["Barcelona", "Puerto La Cruz", "El Tigre", "Anaco"],
    "Apure":           ["San Fernando de Apure", "Guasdualito"],
    "Aragua":          ["Maracay", "Turmero", "La Victoria"],
    "Barinas":         ["Barinas", "Barinitas"],
    "Bolívar":         ["Ciudad Bolívar", "Puerto Ordaz", "Ciudad Guayana", "Upata"],
    "Carabobo":        ["Valencia", "Puerto Cabello", "Guacara"],
    "Cojedes":         ["San Carlos", "Tinaquillo"],
    "Delta Amacuro":   ["Tucupita"],
    "Falcón":          ["Coro", "Punto Fijo"],
    "Guárico":         ["San Juan de los Morros", "Calabozo", "Valle de la Pascua"],
    "Lara":            ["Barquisimeto", "Cabudare", "Carora", "El Tocuyo"],
    "Mérida":          ["Mérida", "El Vigía", "Ejido"],
    "Miranda":         ["Los Teques", "Petare", "Guarenas", "Charallave"],
    "Monagas":         ["Maturín", "Caripito"],
    "Nueva Esparta":   ["La Asunción", "Porlamar", "Pampatar"],
    "Portuguesa":      ["Guanare", "Acarigua", "Araure"],
    "Sucre":           ["Cumaná", "Carúpano"],
    "Táchira":         ["San Cristóbal", "San Antonio del Táchira", "Rubio"],
    "Trujillo":        ["Trujillo", "Valera"],
    "La Guaira":       ["La Guaira", "Maiquetía", "Catia La Mar"],
    "Yaracuy":         ["San Felipe", "Yaritagua"],
    "Zulia":           ["Maracaibo", "Cabimas", "Ciudad Ojeda", "Machiques"],
  },

  US: {
    "Alabama":              ["Birmingham", "Montgomery", "Mobile", "Huntsville", "Tuscaloosa"],
    "Alaska":               ["Anchorage", "Fairbanks", "Juneau"],
    "Arizona":              ["Phoenix", "Tucson", "Mesa", "Chandler", "Scottsdale", "Glendale"],
    "Arkansas":             ["Little Rock", "Fort Smith", "Fayetteville"],
    "California":           ["Los Angeles", "San Diego", "San Jose", "San Francisco", "Fresno", "Sacramento", "Long Beach", "Oakland", "Bakersfield", "Anaheim", "Riverside", "Santa Ana", "Irvine", "Berkeley"],
    "Colorado":             ["Denver", "Colorado Springs", "Aurora", "Fort Collins", "Lakewood", "Boulder"],
    "Connecticut":          ["Bridgeport", "New Haven", "Hartford", "Stamford"],
    "Delaware":             ["Wilmington", "Dover"],
    "District of Columbia": ["Washington"],
    "Florida":              ["Jacksonville", "Miami", "Tampa", "Orlando", "St. Petersburg", "Hialeah", "Tallahassee", "Fort Lauderdale", "Cape Coral"],
    "Georgia":              ["Atlanta", "Augusta", "Columbus", "Savannah", "Athens"],
    "Hawaii":               ["Honolulu", "Hilo", "Kailua"],
    "Idaho":                ["Boise", "Meridian", "Nampa"],
    "Illinois":             ["Chicago", "Aurora", "Joliet", "Naperville", "Rockford", "Springfield"],
    "Indiana":              ["Indianapolis", "Fort Wayne", "Evansville", "South Bend"],
    "Iowa":                 ["Des Moines", "Cedar Rapids", "Davenport"],
    "Kansas":               ["Wichita", "Overland Park", "Kansas City", "Topeka"],
    "Kentucky":             ["Louisville", "Lexington", "Bowling Green"],
    "Louisiana":            ["New Orleans", "Baton Rouge", "Shreveport", "Lafayette"],
    "Maine":                ["Portland", "Lewiston", "Bangor"],
    "Maryland":             ["Baltimore", "Frederick", "Rockville", "Gaithersburg", "Bowie"],
    "Massachusetts":        ["Boston", "Worcester", "Springfield", "Cambridge", "Lowell"],
    "Michigan":             ["Detroit", "Grand Rapids", "Warren", "Ann Arbor", "Lansing"],
    "Minnesota":            ["Minneapolis", "Saint Paul", "Rochester", "Bloomington"],
    "Mississippi":          ["Jackson", "Gulfport", "Southaven"],
    "Missouri":             ["Kansas City", "Saint Louis", "Springfield", "Columbia"],
    "Montana":              ["Billings", "Missoula", "Bozeman", "Helena"],
    "Nebraska":             ["Omaha", "Lincoln", "Bellevue"],
    "Nevada":               ["Las Vegas", "Henderson", "Reno", "North Las Vegas"],
    "New Hampshire":        ["Manchester", "Nashua", "Concord"],
    "New Jersey":           ["Newark", "Jersey City", "Paterson", "Elizabeth", "Edison"],
    "New Mexico":           ["Albuquerque", "Las Cruces", "Santa Fe"],
    "New York":             ["New York", "Buffalo", "Rochester", "Yonkers", "Syracuse", "Albany"],
    "North Carolina":       ["Charlotte", "Raleigh", "Greensboro", "Durham", "Winston-Salem"],
    "North Dakota":         ["Fargo", "Bismarck", "Grand Forks"],
    "Ohio":                 ["Columbus", "Cleveland", "Cincinnati", "Toledo", "Akron", "Dayton"],
    "Oklahoma":             ["Oklahoma City", "Tulsa", "Norman", "Broken Arrow"],
    "Oregon":               ["Portland", "Salem", "Eugene", "Gresham", "Hillsboro"],
    "Pennsylvania":         ["Philadelphia", "Pittsburgh", "Allentown", "Erie"],
    "Rhode Island":         ["Providence", "Warwick", "Cranston"],
    "South Carolina":       ["Charleston", "Columbia", "North Charleston", "Mount Pleasant"],
    "South Dakota":         ["Sioux Falls", "Rapid City"],
    "Tennessee":            ["Nashville", "Memphis", "Knoxville", "Chattanooga"],
    "Texas":                ["Houston", "San Antonio", "Dallas", "Austin", "Fort Worth", "El Paso", "Arlington", "Corpus Christi", "Plano", "Laredo", "Lubbock", "Garland", "Irving"],
    "Utah":                 ["Salt Lake City", "West Valley City", "Provo", "West Jordan"],
    "Vermont":              ["Burlington", "South Burlington", "Rutland"],
    "Virginia":             ["Virginia Beach", "Norfolk", "Chesapeake", "Richmond", "Arlington", "Alexandria"],
    "Washington":           ["Seattle", "Spokane", "Tacoma", "Vancouver", "Bellevue", "Kent"],
    "West Virginia":        ["Charleston", "Huntington", "Morgantown"],
    "Wisconsin":             ["Milwaukee", "Madison", "Green Bay", "Kenosha"],
    "Wyoming":              ["Cheyenne", "Casper"],
  },
};

/** Lista de provincias/estados para un país dado. */
export function getProvinces(country: CountryCode): readonly string[] {
  if (country === "AR") return AR_PROVINCES;
  return Object.keys(PROVINCES_BY_COUNTRY[country] ?? {});
}

/** Lista de ciudades top para una provincia/estado de un país. */
export function getCities(country: CountryCode, province: string): readonly string[] {
  return PROVINCES_BY_COUNTRY[country]?.[province] ?? [];
}

/** Nombre legible de un país desde el code. */
export function getCountryName(code: CountryCode): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

/** Resuelve el code del país desde su nombre legible. Devuelve "AR" como
 *  fallback si no encuentra match — preserva backward compat con jobs viejos
 *  que solo tenían "Ciudad, Provincia" sin país explícito. */
export function getCountryCode(name: string): CountryCode {
  const found = COUNTRIES.find((c) => c.name.toLowerCase() === name.toLowerCase());
  return found?.code ?? "AR";
}
